import { EscalationType, PairedCostRouteItem, RouteItem } from '@aloft/toolkit-shared';

import { ValidationStep } from './types.ts';

export const VALIDATION_STEP_ORDER: ValidationStep[] = [
  'Global',
  'OrderDetails',
  'Market',
  'Comparables',
  'Analysis',
  'Adjustments',
  'Commentary',
  'Reconciliation',
];

export const APPRAISIFY_FIX_DESTINATION_BLOCKED_TOOLTIP = 'Resolve alerts on earlier pages first';

export const VALIDATION_STEP_LABELS: Record<ValidationStep, string> = {
  Global: 'Global',
  OrderDetails: 'Order',
  Market: 'Market',
  Analysis: 'Cost Approach',
  Commentary: 'Commentary',
  Reconciliation: 'Reconciliation',
  Comparables: 'Sales Comparables',
  Adjustments: 'Adjustments',
};

export const routeToStepMap: Partial<Record<RouteItem, ValidationStep>> = {
  details: 'OrderDetails',
  market: 'Market',
  analysis: 'Analysis',
  'cost-approach': 'Analysis',
  'craftsman-cost-approach': 'Analysis',
  commentary: 'Commentary',
  'appraisify-commentary': 'Commentary',
  reconciliation: 'Reconciliation',
  features: 'Comparables',
  'rental-features': 'Comparables',
  adjustments: 'Adjustments',
  'adjustments-grid': 'Adjustments',
};

export const getRouteForValidationStep = (
  step: ValidationStep,
  { includeCostApproach }: { includeCostApproach: boolean },
): PairedCostRouteItem | null => {
  switch (step) {
    case 'Global':
      return null;
    case 'OrderDetails':
      return 'details';
    case 'Market':
      return 'market';
    case 'Comparables':
      return 'features';
    case 'Analysis':
      return includeCostApproach ? 'cost-approach' : null;
    case 'Adjustments':
      return 'adjustments-grid';
    case 'Commentary':
      return 'appraisify-commentary';
    case 'Reconciliation':
      return 'reconciliation';
    default:
      return null;
  }
};

// Map validation keys to user-friendly field names
export const fieldDisplayNames: Record<string, string> = {
  // Order Details
  parcelNumber: 'Parcel Number',
  zoning: 'Zoning',
  zoningCompliance: 'Zoning Compliance',
  legalDescription: 'Legal Description',
  effectiveDate: 'Effective Date',
  intendedUsers: 'Intended Users',
  intendedUse: 'Intended Use',
  totalRenovationBudget: 'Renovation Budget',

  // Analysis
  opinionOfSiteValue: 'Opinion of Site Value',

  // Market
  comps: 'Comparables',

  // Commentary
  estimatedDemand: 'Estimated Demand',
  estimatedExposureTime: 'Estimated Exposure Time',
  marketConditionCommentary: 'Market Condition Commentary',
  priorSalesCommentary: 'Prior Sales Commentary',

  // Reconciliation
  totalCostToCure: 'Total Cost to Cure',
  opinionOfMarketValue: 'Opinion of Market Value',
  reconciliationCommentary: 'Reconciliation Commentary',
  highestAndBestUseCommentary: 'Highest and Best Use Commentary',
};

export const STOP_AND_CALL_VALUES = {
  zoningCompliance: ['Illegal'],
  propertyInterest: ['Leased Fee', 'Leasehold'],
  uadCondition: [5, 6],
  valueTypes: ['subject-to'],
};

export const IMPROVEMENTS_VALIDATION_LABELS = {
  bedrooms: 'Bedrooms',
  fullBaths: 'Full Baths',
  halfBaths: 'Half Baths',
  gla: 'GLA',
  age: 'Property Age',
  siteSize: 'Site Size',
  detachedGarage: 'Detached Garage',
  attachedGarage: 'Attached Garage',
  carport: 'Carport',
};

export const ESCALATION_TYPE_SENTENCES: Record<EscalationType, string> = {
  zoning_compliance_illegal: "Subject's zoning compliance is ILLEGAL.",
  zoning_compliance_non_conforming: "Subject's zoning compliance is LEGAL NONCONFORMING.",
  leasehold_property_interest: "Subject's property interest is LEASEHOLD.",
  leased_fee_property_interest: "Subject's property interest is LEASEHOLD.",
  c5_condition: "Subject's UAD condition is C5.",
  c6_condition: "Subject's UAD condition is C6.",
  value_subject_to: "Subject's value type is SUBJECT-TO.",
};

export const ESCALATION_TYPE_MESSAGES: Record<EscalationType, string> = {
  zoning_compliance_illegal:
    "This means the property or its current use does not comply with local zoning ordinances. This creates significant risk to the lender's collateral and may make the property unfinanceable.\n\nThis requires immediate escalation before proceeding.",
  zoning_compliance_non_conforming:
    "This means the property or its use was legal when established but does not conform to current zoning ordinances. While grandfathered in, this status can affect the property's future use, expansion, and rebuilding rights.\n\nThis requires immediate escalation before proceeding.",
  leased_fee_property_interest:
    "This means the property owner holds fee simple title but the property is currently leased to a tenant. The owner's rights are subject to the existing lease terms, which can affect value and the lender's collateral position.\n\nThis creates additional risk factors that require immediate escalation before proceeding.",
  leasehold_property_interest:
    'This means the borrower does not own the land but only has rights to use the property for a specified lease term.\n\nThis creates additional risk factors that require immediate escalation before proceeding.',
  c5_condition:
    'A C5 rating indicates the property is in poor condition with obvious deferred maintenance and defects, requiring substantial repairs or renovation.',
  c6_condition:
    'A C6 rating indicates the property is in extremely poor condition with severe structural damage and/or deferred maintenance. The property may be uninhabitable or require complete reconstruction.',
  value_subject_to:
    'Per the Letter of Engagement (LOE) for this assignment, the client does not accept Subject-To appraisals or appraisals with hypothetical conditions. All orders are expected to be completed As-Is unless escalated and approved in advance.',
};

export type STOP_AND_CALL_FIELD_NAME =
  | 'propertyInterest'
  | 'zoningCompliance'
  | 'uadCondition'
  | 'valueTypes';
